import { useState } from "react";
import { Slot } from "@radix-ui/react-slot";
import { PROJECTS_REGEX } from "files/hooks/useFiles";
import ProjectPrivilege from "rwb/pages/project/types/enums/privilege";
import useProjectsAccessList from "~/hooks/useProjectsAccessList";
import useUserPermission from "~/hooks/useUserPermission";
import { apiMutate } from "~/utils/apiClient";
import useToast from "./toast";

interface PresignedUrlItem {
  key: string;
  url: string;
  expires_at: string;
}

interface ShareLinksManifestProps {
  fileKey: string;
  children?: React.ReactNode;
}

const ShareLinksManifest = ({ fileKey, children }: ShareLinksManifestProps) => {
  const { showError } = useToast();
  const downloadEnabled = useUserPermission("create:file-browser-download");
  const { data: projectAccessList } = useProjectsAccessList();
  const [isGenerating, setIsGenerating] = useState(false);

  const projectIdMatch = fileKey.match(PROJECTS_REGEX);

  if (!downloadEnabled) {
    return null;
  }

  if (projectIdMatch) {
    const projectId = projectIdMatch.groups?.projectId;
    const accessInfo = projectAccessList?.find(
      (project) => project.project_id === Number(projectId)
    );

    if (!accessInfo?.assigned_privileges.includes(ProjectPrivilege.DOWNLOAD_PROJECT_FILES)) {
      return null;
    }
  }

  const generateManifest = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    const { VITE_RWB_API } = import.meta.env;
    try {
      const items = await apiMutate<PresignedUrlItem[]>(
        `${VITE_RWB_API}/file-browser/generate-download-urls`,
        "POST",
        { keys: [fileKey] }
      );
      const blob = new Blob([JSON.stringify(items, null, 2)], { type: "application/json" });
      const blobUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.style.display = "none";
      anchor.download = "share-links.json";
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(blobUrl);
    } catch {
      showError("Unable to generate share links");
    } finally {
      setIsGenerating(false);
    }
  };

  return <Slot onClick={generateManifest}>{children}</Slot>;
};

export { ShareLinksManifest, type ShareLinksManifestProps };
